/**
 * ポータル: 各種申請（船員アプリ）
 * 有給・慶弔・住所変更などの様式を選んで申請し、自分の申請状況を確認する。
 */
import { useCallback, useEffect, useState } from 'react';
import { AppHeader } from '../../components/AppHeader';
import { PIconChip } from '../../components/PIcon';
import { BottomNav } from '../../components/BottomNav';
import { apiFetch } from '../../services/api';
import { PillBadge, STATUS_PILL, fmtDateTime, inputStyle, labelStyle, EmptyState } from './ui';

interface FieldDef { key: string; label: string; type: string; required?: boolean; options?: string[] }
interface Form { id: string; name: string; description: string | null; fields: FieldDef[]; is_active: number }
interface RequestRow {
  id: string; form_name: string | null; data: Record<string, unknown>;
  status: string; status_label: string; approver_name: string | null; comment: string | null;
  created_at: string; fields?: FieldDef[];
}

export default function PortalRequestsPage() {
  const [tab, setTab] = useState<'apply' | 'mine'>('apply');
  const [forms, setForms] = useState<Form[]>([]);
  const [mine, setMine] = useState<RequestRow[]>([]);
  const [applyForm, setApplyForm] = useState<Form | null>(null);
  const [data, setData] = useState<Record<string, string>>({});
  const [detail, setDetail] = useState<RequestRow | null>(null);
  const [busy, setBusy] = useState(false);

  const loadMine = useCallback(async () => {
    try { const r = await apiFetch<{ data: RequestRow[] }>('/portal/requests?mine=1'); setMine(r.data ?? []); } catch { /* noop */ }
  }, []);
  useEffect(() => {
    apiFetch<{ data: Form[] }>('/portal/requests/forms').then(r => setForms(r.data ?? [])).catch(() => { /* noop */ });
    void loadMine();
  }, [loadMine]);

  const submit = async () => {
    if (!applyForm) return;
    for (const f of applyForm.fields) {
      if (f.required && !(data[f.key] ?? '').trim()) { alert(`「${f.label}」を入力してください`); return; }
    }
    setBusy(true);
    try {
      await apiFetch('/portal/requests', { method: 'POST', body: JSON.stringify({ form_id: applyForm.id, data }) });
      setApplyForm(null); setData({});
      setTab('mine');
      await loadMine();
      alert('申請しました。承認されるまでお待ちください。');
    } catch (e) { alert(e instanceof Error ? e.message : '申請に失敗しました'); }
    finally { setBusy(false); }
  };

  const open = async (id: string) => {
    try { const r = await apiFetch<{ data: RequestRow }>(`/portal/requests/${id}`); setDetail(r.data); }
    catch (e) { alert(e instanceof Error ? e.message : '取得に失敗しました'); }
  };

  const withdraw = async (r: RequestRow) => {
    if (!confirm('この申請を取り下げますか？')) return;
    try {
      await apiFetch(`/portal/requests/${r.id}/action`, { method: 'POST', body: JSON.stringify({ action: 'withdraw' }) });
      setDetail(null);
      await loadMine();
    } catch (e) { alert(e instanceof Error ? e.message : '失敗しました'); }
  };

  return (
    <div style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', background: '#f8fafc' }}>
      <AppHeader title="各種申請">
        <div style={{ display: 'flex', gap: 6, background: '#f1f5f9', borderRadius: 12, padding: 4 }}>
          {([['apply', '申請する'], ['mine', '自分の申請']] as const).map(([key, lbl]) => (
            <button key={key} onClick={() => setTab(key)}
              style={{
                flex: 1, padding: '8px 0', borderRadius: 9, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700,
                background: tab === key ? 'white' : 'transparent', color: tab === key ? '#0f172a' : '#64748b',
                boxShadow: tab === key ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              }}>{lbl}</button>
          ))}
        </div>
      </AppHeader>

      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px 90px' }}>
        {tab === 'apply' ? (
          forms.length === 0 ? (
            <EmptyState img="/portal/empty-requests.png" text="申請できる様式がありません" />
          ) : forms.map(f => (
            <button key={f.id} onClick={() => { setApplyForm(f); setData({}); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', marginBottom: 10,
                background: '#fff', border: '1.5px solid #e4e9f0', borderRadius: 14, padding: '15px 14px', cursor: 'pointer',
              }}>
              <PIconChip name="filecheck" size={42} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14.5, fontWeight: 800, color: '#0f172a' }}>{f.name}</div>
                {f.description && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{f.description}</div>}
              </div>
              <span style={{ color: '#cbd5e1' }}>›</span>
            </button>
          ))
        ) : (
          mine.length === 0 ? (
            <EmptyState img="/portal/empty-requests.png" text="申請はまだありません" />
          ) : mine.map(r => {
            const pill = STATUS_PILL[r.status] ?? { color: '#334155', bg: '#f1f5f9' };
            return (
              <button key={r.id} onClick={() => void open(r.id)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', marginBottom: 10,
                  background: '#fff', border: '1.5px solid #e4e9f0', borderRadius: 14, padding: '13px 14px', cursor: 'pointer',
                }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 14.5, fontWeight: 700, color: '#0f172a', flex: 1 }}>{r.form_name}</span>
                  <PillBadge color={pill.color} bg={pill.bg}>{r.status_label}</PillBadge>
                </div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>{fmtDateTime(r.created_at)}</div>
              </button>
            );
          })
        )}
      </div>

      {/* 申請フォーム */}
      {applyForm && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end' }}
          onClick={() => setApplyForm(null)}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: '18px 18px 0 0', width: '100%', maxHeight: '88dvh', overflowY: 'auto', padding: '18px 18px 28px' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', flex: 1 }}>{applyForm.name}</span>
              <button onClick={() => setApplyForm(null)} style={{ border: 'none', background: 'none', fontSize: 18, color: '#64748b', cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {applyForm.fields.map(f => (
                <div key={f.key}>
                  <label style={labelStyle}>{f.label}{f.required && <span style={{ color: '#dc2626' }}> *</span>}</label>
                  {f.type === 'textarea' ? (
                    <textarea style={{ ...inputStyle, minHeight: 80 }} value={data[f.key] ?? ''} onChange={e => setData(d => ({ ...d, [f.key]: e.target.value }))} />
                  ) : f.type === 'select' ? (
                    <select style={inputStyle} value={data[f.key] ?? ''} onChange={e => setData(d => ({ ...d, [f.key]: e.target.value }))}>
                      <option value="">選択してください</option>
                      {(f.options ?? []).map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input type={f.type === 'date' ? 'date' : f.type === 'number' ? 'number' : 'text'}
                      style={inputStyle} value={data[f.key] ?? ''} onChange={e => setData(d => ({ ...d, [f.key]: e.target.value }))} />
                  )}
                </div>
              ))}
              <button disabled={busy} onClick={() => void submit()}
                style={{ border: 'none', background: busy ? '#64748b' : '#0f766e', color: '#fff', borderRadius: 12, padding: '14px', fontSize: 15, fontWeight: 800, cursor: busy ? 'not-allowed' : 'pointer' }}>
                {busy ? '送信中...' : '申請する'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 申請の詳細 */}
      {detail && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end' }}
          onClick={() => setDetail(null)}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: '18px 18px 0 0', width: '100%', maxHeight: '86dvh', overflowY: 'auto', padding: '18px 18px 28px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <span style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', flex: 1 }}>{detail.form_name}</span>
              {(() => { const p = STATUS_PILL[detail.status] ?? { color: '#334155', bg: '#f1f5f9' }; return <PillBadge color={p.color} bg={p.bg}>{detail.status_label}</PillBadge>; })()}
              <button onClick={() => setDetail(null)} style={{ border: 'none', background: 'none', fontSize: 18, color: '#64748b', cursor: 'pointer' }}>✕</button>
            </div>
            {(detail.fields ?? []).map(f => (
              <div key={f.key} style={{ display: 'flex', fontSize: 13.5, padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ width: 120, color: '#64748b', flexShrink: 0 }}>{f.label}</span>
                <span style={{ color: '#0f172a', whiteSpace: 'pre-wrap' }}>{String(detail.data[f.key] ?? '—')}</span>
              </div>
            ))}
            {detail.comment && (
              <div style={{ fontSize: 13, color: '#4b5563', marginTop: 10 }}>処理コメント: {detail.comment}（{detail.approver_name}）</div>
            )}
            {detail.status === 'pending' && (
              <button onClick={() => void withdraw(detail)}
                style={{ width: '100%', marginTop: 14, border: '1.5px solid #fecaca', background: '#fef2f2', color: '#dc2626', borderRadius: 12, padding: '12px', fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>
                申請を取り下げる
              </button>
            )}
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
}
